import { useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { adminService } from '@/services/admin';
import type { StudentRecord, ClassSummary } from '@/types';
import { resolvePersonPhoto } from '@/utils/avatars';
import { AvatarUploader } from '@/components/ui/AvatarUploader';
import { PhotoLightbox, PhotoThumb } from '@/components/ui/PhotoLightbox';
import { TypeConfirmDialog } from '@/components/ui/TypeConfirmDialog';
import { ChangeRegistrationDialog } from '@/components/admin/ChangeRegistrationDialog';

const UNASSIGNED_FILTER = 'unassigned';
const GRADE_PREFIX = 'grade:';
type StatusFilter = 'active' | 'left' | 'all';

const selectClassName =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-base sm:text-sm min-h-[40px] shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30';

const resolveErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string; error?: string; errors?: Record<string, string[]> } } }).response;
    const errors = response?.data?.errors ?? {};
    const fieldError =
      errors.rollNumber?.[0] ??
      errors.classId?.[0] ??
      errors.parentPhone?.[0] ??
      errors.fatherName?.[0] ??
      errors.cnic?.[0] ??
      errors.bForm?.[0] ??
      errors.admissionDate?.[0];
    return fieldError ?? response?.data?.message ?? response?.data?.error ?? fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
};

const classOptionLabel = (classItem: ClassSummary) =>
  classItem.sectionName
    ? `${classItem.gradeLevel} - ${classItem.sectionName}`
    : classItem.name || classItem.gradeLevel;

const FORM_UNASSIGNED = 'unassigned';

const localToday = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const admissionDateValue = (value?: string | null) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const isPkMobile = (value: string) => /^03\d{9}$/.test(value.replace(/[\s-]/g, ''));

const isNationalId = (value: string) => value.replace(/\D+/g, '').length === 13;

const phoneKeystrokes = (value: string) => value.replace(/[^\d\s-]/g, '');

const isLegacyPhonePlaceholder = (value: string) => /^n\/?a$/i.test(value.trim());

type SortKey = 'rollNumber' | 'student' | 'className';

const compareReg = (a: StudentRecord, b: StudentRecord) => {
  const aDigits = /^\d+$/.test(a.rollNumber);
  const bDigits = /^\d+$/.test(b.rollNumber);
  if (aDigits && bDigits) return Number(a.rollNumber) - Number(b.rollNumber);
  return a.rollNumber.localeCompare(b.rollNumber, undefined, { numeric: true });
};

const FieldLabel = ({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
}) => (
  <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-slate-700">
    {children}
    {required ? (
      <span className="ml-0.5 text-rose-600" aria-hidden="true">
        *
      </span>
    ) : null}
  </label>
);

const emptyForm = {
  firstName: '',
  lastName: '',
  classId: '',
  rollNumber: '',
  photo: '',
  parentPhone: '',
  address: '',
  bloodGroup: '',
  cnic: '',
  bForm: '',
  fatherName: '',
  gender: '',
  admissionDate: '',
};

export default function AdminStudentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [pendingLeave, setPendingLeave] = useState<StudentRecord | null>(null);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('rollNumber');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<StudentRecord | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [changeOpen, setChangeOpen] = useState(false);
  const [confirmedOverride, setConfirmedOverride] = useState<string | null>(null);
  const [changePending, setChangePending] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: students, isLoading: loadingStudents } = useQuery<StudentRecord[]>({
    queryKey: ['admin-students'],
    queryFn: adminService.listStudents,
  });

  const { data: classes = [] } = useQuery<ClassSummary[]>({
    queryKey: ['admin-classes'],
    queryFn: adminService.listClasses,
  });

  const classList = Array.isArray(classes) ? classes : [];
  const studentList = Array.isArray(students) ? students : [];
  const activeCount = studentList.filter((student) => student.status !== 'Inactive').length;
  const leftCount = studentList.filter((student) => student.status === 'Inactive').length;
  const unassignedCount = studentList.filter((student) => student.status !== 'Inactive' && !student.classId).length;

  useEffect(() => {
    const classParam = searchParams.get('class');
    const gradeParam = searchParams.get('grade');
    if (classParam) {
      setClassFilter(classParam);
      return;
    }
    if (gradeParam) {
      setClassFilter(`${GRADE_PREFIX}${gradeParam}`);
    }
  }, [searchParams]);

  const invalidateRoster = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-students'] });
    queryClient.invalidateQueries({ queryKey: ['admin-classes'] });
    queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
    queryClient.invalidateQueries({ queryKey: ['admin-class-levels'] });
  };

  const createMutation = useMutation({
    mutationFn: adminService.createStudent,
    onSuccess: () => {
      invalidateRoster();
      closeModal();
    },
    onError: (error) => alert(resolveErrorMessage(error, 'Unable to create student.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      adminService.updateStudent(id, data),
    onSuccess: () => {
      invalidateRoster();
      closeModal();
    },
    onError: (error) => alert(resolveErrorMessage(error, 'Unable to update student.')),
  });

  const deleteMutation = useMutation({
    mutationFn: adminService.deleteStudent,
    onSuccess: () => {
      setPendingLeave(null);
      setDeleteTyped('');
      invalidateRoster();
    },
    onError: (error) => alert(resolveErrorMessage(error, 'Unable to mark student left.')),
  });

  const restoreMutation = useMutation({
    mutationFn: adminService.restoreStudent,
    onSuccess: invalidateRoster,
    onError: (error) => alert(resolveErrorMessage(error, 'Unable to restore student.')),
  });

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return studentList.filter((student: StudentRecord) => {
      const matchesSearch =
        `${student.firstName} ${student.lastName}`.toLowerCase().includes(term) ||
        student.rollNumber.toLowerCase().includes(term);
      if (!matchesSearch) return false;
      if (statusFilter === 'active' && student.status === 'Inactive') return false;
      if (statusFilter === 'left' && student.status !== 'Inactive') return false;
      if (!classFilter) return true;
      if (classFilter === UNASSIGNED_FILTER) return !student.classId;
      if (classFilter.startsWith(GRADE_PREFIX)) {
        return student.gradeLevel === classFilter.slice(GRADE_PREFIX.length);
      }
      return student.classId === classFilter;
    });
  }, [studentList, search, classFilter, statusFilter]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'rollNumber') cmp = compareReg(a, b);
      else if (sortKey === 'student') {
        cmp = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
      } else {
        cmp = (a.className || 'Unassigned').localeCompare(b.className || 'Unassigned');
      }
      return cmp * dir;
    });
    return rows;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir('asc');
  };

  const sortMark = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingStudent(null);
    setFormData(emptyForm);
    setChangeOpen(false);
    setConfirmedOverride(null);
  };

  const openCreateModal = async () => {
    setEditingStudent(null);
    setConfirmedOverride(null);
    try {
      const data = await adminService.nextRegistration();
      const allotted = (data.registration || '').trim();
      if (!allotted) {
        alert('Unable to allot a registration number.');
        return;
      }
      setFormData({ ...emptyForm, admissionDate: localToday(), rollNumber: allotted });
      setIsModalOpen(true);
    } catch {
      alert('Unable to allot a registration number.');
    }
  };

  const openEditModal = (student: StudentRecord) => {
    setEditingStudent(student);
    setFormData({
      firstName: student.firstName,
      lastName: student.lastName,
      classId: student.classId || FORM_UNASSIGNED,
      rollNumber: student.rollNumber,
      photo: student.photo || '',
      parentPhone: student.parentPhone || '',
      address: student.address || '',
      bloodGroup: student.bloodGroup || '',
      cnic: student.cnic || '',
      bForm: student.bForm || '',
      fatherName: student.fatherName || '',
      gender: student.gender || '',
      admissionDate: admissionDateValue(student.admissionDate) || localToday(),
    });
    setIsModalOpen(true);
  };

  const handleUpload = async (file: File) => {
    const data = await adminService.uploadAvatar(file);
    setFormData((prev) => ({ ...prev, photo: data.url }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!formData.firstName || !formData.lastName) return;
    if (!editingStudent && !formData.rollNumber) {
      alert('Unable to allot a registration number.');
      return;
    }
    if (!formData.classId) {
      alert('Select a class.');
      return;
    }
    const phone = formData.parentPhone.trim();
    if (phone && !isLegacyPhonePlaceholder(phone) && !isPkMobile(phone)) {
      alert('Parent phone must be a Pakistan mobile (03 and 9 more digits), or left empty.');
      return;
    }
    if (editingStudent) {
      if (formData.cnic.trim() && !isNationalId(formData.cnic)) {
        alert('CNIC must be 13 digits (dashes allowed), or left empty.');
        return;
      }
      if (formData.bForm.trim() && !isNationalId(formData.bForm)) {
        alert('B-Form must be 13 digits (dashes allowed), or left empty.');
        return;
      }
    }

    const payload: Record<string, unknown> = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      classId: formData.classId === FORM_UNASSIGNED ? null : formData.classId,
      photo: formData.photo,
      parentPhone: formData.parentPhone.trim(),
      fatherName: formData.fatherName.trim(),
      gender: formData.gender || null,
      admissionDate: formData.admissionDate || localToday(),
    };

    if (editingStudent) {
      payload.cnic = formData.cnic.trim();
      payload.bForm = formData.bForm.trim();
      payload.address = formData.address;
      updateMutation.mutate({ id: editingStudent.id, data: payload });
    } else {
      if (confirmedOverride) {
        payload.confirmedRegistration = confirmedOverride;
      }
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (student: StudentRecord) => {
    setDeleteTyped('');
    setPendingLeave(student);
  };

  const confirmLeave = () => {
    if (!pendingLeave || deleteTyped !== 'DELETE') return;
    deleteMutation.mutate(pendingLeave.id);
  };

  const handleFilterChange = (value: string) => {
    setClassFilter(value);
    const next = new URLSearchParams(searchParams);
    next.delete('class');
    next.delete('grade');
    if (value === UNASSIGNED_FILTER) next.set('class', UNASSIGNED_FILTER);
    else if (value.startsWith(GRADE_PREFIX)) next.set('grade', value.slice(GRADE_PREFIX.length));
    else if (value) next.set('class', value);
    setSearchParams(next, { replace: true });
  };

  const formClassSelectOptions = (
    <>
      <option value="">Select class</option>
      {classList.map((classItem) => (
        <option key={classItem.id} value={classItem.id}>
          {classOptionLabel(classItem)}
        </option>
      ))}
      <option value={FORM_UNASSIGNED}>Unassigned</option>
    </>
  );

  const gradeFilterLabel = classFilter.startsWith(GRADE_PREFIX)
    ? classFilter.slice(GRADE_PREFIX.length)
    : '';

  return (
    <DashboardShell title="Student Directory" role="admin">
      <Card
        title={loadingStudents ? 'Students' : `Students (${activeCount} active)`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => navigate('/admin/students/class-transfer')}>
              Class assignment
            </Button>
            <Button variant="primary" onClick={openCreateModal}>
              Add Student
            </Button>
          </div>
        }
      >
        <div className="mb-4 grid gap-3 md:grid-cols-3">
          <input
            type="search"
            placeholder="Search students by name..."
            className={selectClassName}
            value={search}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
          />
          <select
            className={selectClassName}
            value={classFilter.startsWith(GRADE_PREFIX) ? classFilter : classFilter}
            onChange={(event) => handleFilterChange(event.target.value)}
          >
            <option value="">All Classes</option>
            <option value={UNASSIGNED_FILTER}>Unassigned ({loadingStudents ? '…' : unassignedCount})</option>
            {gradeFilterLabel && (
              <option value={classFilter}>{gradeFilterLabel} (all sections)</option>
            )}
            {classList.map((classItem) => (
              <option key={classItem.id} value={classItem.id}>
                {classOptionLabel(classItem)}
              </option>
            ))}
          </select>
          <select
            className={selectClassName}
            aria-label="Student status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
          >
            <option value="active">Active ({loadingStudents ? '…' : activeCount})</option>
            <option value="left">Left ({loadingStudents ? '…' : leftCount})</option>
            <option value="all">All ({loadingStudents ? '…' : studentList.length})</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <button type="button" className="uppercase tracking-wide" onClick={() => toggleSort('rollNumber')}>
                    Reg. no.{sortMark('rollNumber')}
                  </button>
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <button type="button" className="uppercase tracking-wide" onClick={() => toggleSort('student')}>
                    Student{sortMark('student')}
                  </button>
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <button type="button" className="uppercase tracking-wide" onClick={() => toggleSort('className')}>
                    Class{sortMark('className')}
                  </button>
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {sorted.map((student: StudentRecord) => (
                <tr key={student.id} className="bg-white">
                  <td className="px-3 py-3 text-sm font-semibold text-slate-900">{student.rollNumber}</td>
                  <td className="px-3 py-3 text-sm font-medium text-slate-900">
                    <div className="flex items-center gap-3">
                      <PhotoThumb
                        src={resolvePersonPhoto(student, 'student')}
                        onClick={() => setPreviewSrc(resolvePersonPhoto(student, 'student'))}
                      />
                      <div>
                        <span>
                          {student.firstName} {student.lastName}
                        </span>
                        <div className="text-xs text-slate-400">
                          {student.gender || '—'}
                          {student.status === 'Inactive' ? ' · Left' : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-600">{student.className || 'Unassigned'}</td>
                  <td className="px-3 py-3 text-right text-sm">
                    <button onClick={() => openEditModal(student)} className="mr-3 text-blue-600 hover:text-blue-800">
                      Edit
                    </button>
                    {student.status === 'Inactive' ? (
                      <button
                        onClick={() => restoreMutation.mutate(student.id)}
                        className="text-emerald-600 hover:text-emerald-800"
                        disabled={restoreMutation.isPending}
                      >
                        Restore
                      </button>
                    ) : (
                      <button onClick={() => handleDelete(student)} className="text-rose-600 hover:text-rose-800">
                        Mark left
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!sorted.length && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-sm text-slate-500">
                    {loadingStudents
                      ? 'Loading students…'
                      : 'No students match these filters. Clear search or add a student.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingStudent ? 'Edit Student' : 'Add New Student'}
        closeOnEscape={!changeOpen}
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : editingStudent
                  ? 'Update Student'
                  : 'Create Student'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <FieldLabel>Photo</FieldLabel>
            <div className="mb-4 flex justify-center">
              <AvatarUploader currentPhotoUrl={formData.photo} onUpload={handleUpload} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel htmlFor="firstName" required>
                First Name
              </FieldLabel>
              <input
                id="firstName"
                type="text"
                required
                className={selectClassName}
                value={formData.firstName}
                onChange={(event) => setFormData({ ...formData, firstName: event.target.value })}
              />
            </div>
            <div>
              <FieldLabel htmlFor="lastName" required>
                Last Name
              </FieldLabel>
              <input
                id="lastName"
                type="text"
                required
                className={selectClassName}
                value={formData.lastName}
                onChange={(event) => setFormData({ ...formData, lastName: event.target.value })}
              />
            </div>
          </div>
          <div>
            <FieldLabel htmlFor="fatherName">Father's Name</FieldLabel>
            <input
              id="fatherName"
              type="text"
              className={selectClassName}
              value={formData.fatherName}
              onChange={(event) => setFormData({ ...formData, fatherName: event.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel htmlFor="classId" required>
                Class
              </FieldLabel>
              <select
                id="classId"
                required
                className={selectClassName}
                value={formData.classId}
                onChange={(event) => setFormData({ ...formData, classId: event.target.value })}
              >
                {formClassSelectOptions}
              </select>
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between gap-3">
                <FieldLabel htmlFor="rollNumber" required>
                  Registration no.
                </FieldLabel>
                {!editingStudent || editingStudent.status !== 'Inactive' ? (
                  <button
                    type="button"
                    className="text-xs text-slate-500 underline decoration-slate-300 hover:text-slate-700"
                    onClick={() => setChangeOpen(true)}
                  >
                    Change
                  </button>
                ) : null}
              </div>
              <input
                id="rollNumber"
                type="text"
                readOnly
                required
                aria-readonly="true"
                className={`${selectClassName} cursor-not-allowed bg-slate-50 text-slate-700`}
                value={formData.rollNumber}
              />
              <p className="mt-1 text-xs text-slate-500">
                {editingStudent
                  ? 'School registration, unique for the whole school. Not class serial 1, 2, 3.'
                  : 'Assigned automatically. School registration, unique for the whole school. Not class serial 1, 2, 3.'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel htmlFor="gender">Gender</FieldLabel>
              <select
                id="gender"
                className={selectClassName}
                value={formData.gender}
                onChange={(event) => setFormData({ ...formData, gender: event.target.value })}
              >
                <option value="">—</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div>
              <FieldLabel htmlFor="parentPhone">Parent Phone</FieldLabel>
              <input
                id="parentPhone"
                type="tel"
                inputMode="numeric"
                placeholder="optional"
                className={selectClassName}
                value={formData.parentPhone}
                onChange={(event) => {
                  const next = event.target.value;
                  const filtered =
                    editingStudent && isLegacyPhonePlaceholder(next) ? next : phoneKeystrokes(next);
                  setFormData({ ...formData, parentPhone: filtered });
                }}
              />
            </div>
          </div>
          <div>
            <FieldLabel htmlFor="admissionDate">Admission date</FieldLabel>
            <input
              id="admissionDate"
              type="date"
              className={selectClassName}
              value={formData.admissionDate}
              onChange={(event) => setFormData({ ...formData, admissionDate: event.target.value })}
            />
            {!editingStudent ? (
              <p className="mt-1 text-xs text-slate-500">Defaults to today if left as-is.</p>
            ) : null}
          </div>
          {editingStudent ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel htmlFor="cnic">Father's CNIC</FieldLabel>
                  <input
                    id="cnic"
                    type="text"
                    className={selectClassName}
                    value={formData.cnic}
                    onChange={(event) => setFormData({ ...formData, cnic: event.target.value })}
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="bForm">B-Form</FieldLabel>
                  <input
                    id="bForm"
                    type="text"
                    className={selectClassName}
                    value={formData.bForm}
                    onChange={(event) => setFormData({ ...formData, bForm: event.target.value })}
                  />
                </div>
              </div>
              <div>
                <FieldLabel htmlFor="address">Address</FieldLabel>
                <textarea
                  id="address"
                  rows={2}
                  className={selectClassName}
                  value={formData.address}
                  onChange={(event) => setFormData({ ...formData, address: event.target.value })}
                />
              </div>
            </>
          ) : null}
        </form>
      </Modal>

      <ChangeRegistrationDialog
        isOpen={changeOpen}
        currentNumber={formData.rollNumber}
        ignoreStudentId={editingStudent?.id ?? null}
        pending={changePending}
        onClose={() => setChangeOpen(false)}
        onApply={async (nextNumber) => {
          if (editingStudent) {
            setChangePending(true);
            try {
              const updated = await adminService.changeRegistration(editingStudent.id, nextNumber);
              setEditingStudent({ ...editingStudent, rollNumber: updated.rollNumber });
              setFormData((prev) => ({ ...prev, rollNumber: updated.rollNumber }));
              invalidateRoster();
              setChangeOpen(false);
            } finally {
              setChangePending(false);
            }
            return;
          }
          setConfirmedOverride(nextNumber);
          setFormData((prev) => ({ ...prev, rollNumber: nextNumber }));
          setChangeOpen(false);
        }}
      />

      <TypeConfirmDialog
        isOpen={Boolean(pendingLeave)}
        title="Mark student left"
        description="This marks the student inactive. The record and attendance history stay. They leave the active roster. Unassigned is only for students with no class."
        confirmWord="DELETE"
        confirmLabel="Mark left"
        pending={deleteMutation.isPending}
        pendingLabel="Saving…"
        confirmValue={deleteTyped}
        onConfirmValueChange={setDeleteTyped}
        onCancel={() => {
          setPendingLeave(null);
          setDeleteTyped('');
        }}
        onConfirm={confirmLeave}
      />

      <PhotoLightbox src={previewSrc} onClose={() => setPreviewSrc(null)} />
    </DashboardShell>
  );
}
